import CloseIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Box, IconButton, Stack, Typography, type SxProps, type Theme } from "@mui/material";
import { useState } from "react";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { parseUrlFromMarkdown } from "@entrilia/shared/utilities/urlHelper";
import UrlFieldLink from "../UrlFieldLink";
import EditUrlDialog from "./EditUrlDialog";

interface Props {
  value: unknown;
  onChange: (markdownUrl: string | undefined) => void;
  disabled?: boolean;
  hasError?: boolean;
  validationError?: string;
  sx?: SxProps<Theme>;
}

const UrlFieldEdit = ({ value, onChange, disabled, hasError, validationError, sx }: Props) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { title, url } = parseUrlFromMarkdown(stringifyValue(value));

  const handleSave = (newTitle: string, newUrl: string) => {
    const markdownUrl = `[${newTitle}](${newUrl})`;
    onChange(markdownUrl);
  };

  const handleClear = () => {
    onChange(undefined);
  };

  return (
    <>
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <Stack
          direction="row"
          spacing={1}
          sx={[
            {
              alignItems: "center",
              width: "100%",
              border: (t) => (hasError ? `1px solid ${t.palette.error.main}` : 0),
              pl: 1,
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>{url && <UrlFieldLink title={title} url={url} noWrap />}</Box>

          <IconButton onClick={handleClear} disabled={disabled}>
            <CloseIcon />
          </IconButton>
          <IconButton onClick={() => setEditDialogOpen(true)} disabled={disabled}>
            <EditIcon />
          </IconButton>
        </Stack>

        {validationError && (
          <Typography variant="caption" color="error" sx={{ px: 1 }}>
            {validationError}
          </Typography>
        )}
      </Stack>
      {editDialogOpen && (
        <EditUrlDialog
          onClose={() => setEditDialogOpen(false)}
          title={title}
          url={url?.href ?? ""}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default UrlFieldEdit;
